"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";
import type { CarouselApi } from "@/components/ui/carousel";

const clients = [
  {
    name: "WesTrac Caterpillar",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343154/clients/bkxrbphrbd4d69s1od4x.png",
  },
  {
    name: "Roy Hill",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343153/clients/awh6vvxmiy00mj2dyeht.png",
  },
  {
    name: "Komatsu",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343149/clients/lwqvo3nnq8kxyhv4ykyn.png",
  },
  {
    name: "BHP",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343146/clients/rykbivtsetu92udlzt1t.png",
  },
  {
    name: "Epiroc",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343147/clients/ihxqcjmewppzkcasupfi.png",
  },
  {
    name: "Fortescue",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343148/clients/zggy3kyanwsau7iyvarx.png",
  },
  {
    name: "Rio Tinto",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343150/clients/xsxoaftbvmtwfc2ltp6n.png",
  },
  {
    name: "KAZ Minerals",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343148/clients/spwaetaul9e2gfr0ikmx.png",
  },
  {
    name: "Glencore",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343148/clients/yyjdt6shijgoc1cmwq0u.png",
  },
  {
    name: "Mineral Resources",
    logo: "https://res.cloudinary.com/djowccubk/image/upload/v1736343150/clients/rez8fws4cv4kf3z3dwhl.png",
  },
];

function OurClients() {
  const [api, setApi] = useState<CarouselApi>();
  const [isHovered, setIsHovered] = useState(false);
  const plugin = useRef(Autoplay({ delay: 4500, stopOnInteraction: false }));

  React.useEffect(() => {
    if (!api) return;

    if (isHovered) {
      api.scrollTo(api.selectedScrollSnap());
      plugin.current.stop();
    } else {
      plugin.current.play();
    }
  }, [api, isHovered]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <section className='w-full py-4 md:p-0 md:h-[calc(20vh-65px)] flex items-center justify-center bg-white'>
      <div className='hidden md:flex items-center justify-end gap-8 pl-10'>
        <p className='text-muted text-right'>Our Customers Include</p>
        <div className='h-16 w-[1px] bg-gray-200'></div>
      </div>
      <div className='w-full'>
        <div className='select-none'>
          <TooltipProvider>
            <Carousel
              setApi={setApi}
              opts={{
                align: "start",
                loop: true,
                skipSnaps: false,
                containScroll: "trimSnaps",
                dragFree: true,
              }}
              plugins={[plugin.current]}
              className='w-full cursor-grab active:cursor-grabbing'
            >
              <CarouselContent className='-ml-4 md:-ml-6'>
                {clients.map((client, index) => (
                  <CarouselItem
                    key={index}
                    className={cn(
                      "pl-4 md:pl-6 basis-full",
                      "basis-1/2",
                      "md:basis-1/4",
                      "lg:basis-1/5",
                      "xl:basis-1/5"
                    )}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className='relative w-full aspect-[500/146] bg-white rounded-lg flex items-center justify-center px-4 md:px-6 select-none cursor-grab active:cursor-grabbing'
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                          draggable='false'
                          onDoubleClick={(e) => e.preventDefault()}
                        >
                          <div className='relative w-[80%] h-[80%]'>
                            <Image
                              src={client.logo}
                              alt={client.name}
                              fill
                              className='object-contain pointer-events-none select-none'
                              draggable='false'
                              style={{
                                userSelect: "none",
                                WebkitUserSelect: "none",
                              }}
                              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                            />
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className='select-none'>
                        <p>{client.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </TooltipProvider>
        </div>
      </div>
    </section>
  );
}

export default OurClients;
